// Fleet telemetry model — ported from sparkride-frontend/public/heatmap.html.
//
// This is the ONLY module that knows the sparkapi payload shape. It is pure:
// no Leaflet, no Svelte, no fetch. The store (stores/fleet.svelte.ts) does I/O,
// the fleetModes/* registry decides what each heatmap draws; both consume the
// FleetUnit[] this module produces.
//
// Two upstream defects are compensated for here rather than "fixed", because
// the compensation is what makes the data usable (both verified against the
// live API on 2026-08-17):
//
//   1. Latitudes come back POSITIVE for Kenya — the API reports "1.282058" for
//      Nairobi, which is at -1.28. Southern-hemisphere values in the 0..5 band
//      are mirrored back below the equator (see normalizeCoords).
//   2. `lastlogtime` runs ~4h ahead of real UTC. Freshness is therefore measured
//      against the newest record in the batch, never against Date.now() — a
//      wall-clock comparison marks the entire fleet stale. For the same reason
//      "today" is derived from that same feed clock, so both tests read from one
//      consistent time base.

import { ASSET, MAX_SPEED_KMH, PHASE } from './constants';
import { bearingDeg, haversineM } from './geo';
import { getPowerhiveBackendUrl } from './powerhiveApi';

/** Tunables lifted out of heatmap.html's render loop so they can be changed in one place. */
export const FLEET = {
  get BATTERY_API(): string {
    return `${getPowerhiveBackendUrl()}/api/v1/telemetry/batteries/locations`;
  },
  get CABINET_API(): string {
    return `${getPowerhiveBackendUrl()}/api/v1/telemetry/cabinets/state`;
  },

  /** A unit is "in motion" only if its ping is this fresh (minutes, vs the feed clock). */
  FRESH_PING_MIN: 5,
  /** A stationary unit counts as a working bike (not dormant stock) above this 30d mileage. */
  PARKED_MIN_KM_30D: 10,
  /** Kenya bounding box — anything outside is a bad fix and is rejected. */
  BBOX: { latMin: -5, latMax: 5, lngMin: 33, lngMax: 42 },
  /** Latitudes in this positive band are upstream sign errors; mirror them south. */
  LAT_FLIP_MAX: 5,
  /**
   * Anything reporting a `lastlogtime` more than this far ahead of wall-clock
   * is a broken device clock, not the feed's normal ~4h skew — rejected the
   * same way a bad coordinate fix is.
   */
  MAX_LOG_SKEW_H: 24,

  /** Pin scale vs zoom: 1.0x at zoom 13, clamped to [0.70, 1.35]. */
  ZOOM_ANCHOR: 13,
  ZOOM_STEP: 0.09,
  ZOOM_SCALE_MIN: 0.7,
  ZOOM_SCALE_MAX: 1.35,

  /** Control defaults (the slider start positions in the source page). */
  DEFAULT_DOT_PX: 9,
  DEFAULT_RING_PX: 3,
  DEFAULT_RADIUS_PX: 37,
  DEFAULT_BLUR_PX: 22,
  /** Marker-cluster grouping radius, in screen pixels. */
  DEFAULT_CLUSTER_RADIUS_PX: 35,
  /** Zoom level at which clustering stops and all pins are rendered individually. */
  DEFAULT_CLUSTER_UNTIL_ZOOM: 13,

  /** State-of-charge bands: >=60 healthy, >=26 mid, below that critical. */
  SOC_GOOD: 60,
  SOC_LOW: 26,
  /** Within this, a rider reaches rideable stock without drama — deficit is
   *  served. A guess about Nairobi swap behaviour, not a measurement. */
  SWAP_REACH_M: 800,
  /** Beyond this, treat the deficit as wholly unserved. Also a guess. */
  SWAP_FAR_M: 3000,
  /**
   * At or above this a seated pack is treated as done charging. Matches the
   * cabinet firmware's own "full" report — it stops constant-current well
   * before 100%, so waiting for 100 would mark ready packs as still charging.
   */
  SOC_FULL: 95,
  /** Above this speed a moving pin is drawn rose instead of purple. */
  FAST_KMH: 35,
  /** Active motor current discharge threshold (Amperes, negative value). */
  DISCHARGE_AMPS_THRESHOLD: -1.5,
  /** Minimum kinetic speed in km/h to count as motion without active electrical load. */
  MOTION_SPEED_THRESHOLD_KMH: 3.0,
  /**
   * A unit must travel at least this far between polls before its heading is
   * re-derived. GPS jitter on a stationary bike is a few metres per ping, which
   * would otherwise spin its chevron to a random bearing every 30s.
   */
  HEADING_MIN_M: 12,

  /**
   * No ping in this many days marks a unit "stale" — a connectivity or
   * hardware problem, tracked independently of its last-known SoC or state.
   */
  STALE_DAYS: 3,

  /**
   * `moving` is decided from displacement over this trailing window, not a
   * single packet — see deriveMotion. Three polls at the 30s cadence: long
   * enough that a single `speed: 0` reading can't flip the state, short
   * enough that a bike that genuinely parks drops out within one window.
   */
  MOVING_WINDOW_MIN: 3,
  /** A unit must cover at least this far within MOVING_WINDOW_MIN to count
   *  as moving — a few bike-lengths past ordinary GPS jitter. */
  MOVING_MIN_M: 80,

  /** The fleet operates in Kenya; "today" is a Nairobi day, not a UTC one. */
  TZ: 'Africa/Nairobi',
} as const;

// ── Upstream payload shapes ───────────────────────────────────────────────
// Numeric fields arrive inconsistently as strings or numbers, hence the unions.

/** Which public payload shape produced a normalized record.  This is kept on
 * normalized rows (rather than inferred from fields) because the Rust feed is
 * deliberately fail-closed on registration while the legacy feed predates
 * that field and must remain compatible during rollback. */
export type BatteryFeed = 'rust' | 'legacy';
/** Stable, non-PII deployment values emitted by the Rust fleet feed. */
export type DeploymentState = 'on_bike' | 'on_bike_inferred' | 'unassigned' | 'unknown';
/** Why the backend inferred a bike assignment. */
export type DeploymentEvidence =
  | 'incomplete_dispatch_with_active_telemetry'
  | 'active_telemetry_without_dispatch_event';

export interface BatteryRecord {
  batteryId?: string;
  battserial?: string;
  latitude?: string | number | null;
  longitude?: string | number | null;
  speed?: string | number | null;
  charge?: string | number | null;
  current?: string | number | null;
  power?: string | number | null;
  direction?: string | number | null;
  dischargeMos?: string | null;
  chargeMos?: string | null;
  registered?: boolean | null;
  deploymentState?: DeploymentState | null;
  deploymentEvidence?: DeploymentEvidence | null;
  deploymentEventTime?: string | null;
  lastlogtime?: string | null;
  effKm30d?: string | number | null;
  effPairedSwaps?: string | number | null;
  /** Internal provenance set by normalizeBatteryPayload; not API data. */
  feed?: BatteryFeed;
}

export interface CabinetDoor {
  slot?: number | null;
  batterySn?: string | null;
  /** GPS/module identity used for telemetry requests when mapped. */
  batteryId?: string | null;
  soc?: number | null;
  batteryPresent?: boolean;
  status?: string | null;
}

export interface Cabinet {
  cabinet_id?: string;
  cabinet_name?: string | null;
  cabinetLocation?: string | null;
  location?: string | null;
  capacity?: number | null;
  latitude?: string | number | null;
  longitude?: string | number | null;
  doors?: CabinetDoor[] | null;
  last_log_time?: string | null;
  lastConected?: string | null;
}

/** Normalize both the Rust flat array and the legacy SparkAPI envelope. */
export function normalizeBatteryPayload(payload: unknown): BatteryRecord[] {
  const isRust =
    Array.isArray(payload) ||
    (payload && typeof payload === 'object' && Array.isArray((payload as { data?: unknown }).data));
  const rows = Array.isArray(payload)
    ? payload
    : isRust
      ? (payload as { data: unknown[] }).data
      : payload &&
          typeof payload === 'object' &&
          Array.isArray((payload as { batterylocationdata?: unknown }).batterylocationdata)
        ? (payload as { batterylocationdata: unknown[] }).batterylocationdata
        : [];
  const feed: BatteryFeed = isRust ? 'rust' : 'legacy';

  return rows.flatMap((value) => {
    if (!value || typeof value !== 'object') return [];
    const row = value as BatteryRecord;
    // Keep batteryId (the GPS-module identity) distinct from battserial (the
    // physical pack identity). Seating and marker identity use battserial;
    // batteryId remains available for diagnostics and API calls.
    return [{ ...row, feed }];
  });
}

/** Return the normalized physical-pack identity used for seating and markers. */
export function normalizedBatterySerial(
  record: Pick<BatteryRecord, 'battserial' | 'batteryId'>,
): string {
  const serial = typeof record.battserial === 'string' ? record.battserial.trim() : '';
  if (serial) return serial.toUpperCase();
  const gpsId = typeof record.batteryId === 'string' ? record.batteryId.trim() : '';
  return gpsId.toUpperCase();
}

/** Normalize both the Rust array and an optional `{ data: [...] }` envelope. */
export function normalizeCabinetPayload(payload: unknown): Cabinet[] {
  const rows = Array.isArray(payload)
    ? payload
    : payload && typeof payload === 'object' && Array.isArray((payload as { data?: unknown }).data)
      ? (payload as { data: unknown[] }).data
      : [];
  return rows.flatMap((value) => {
    if (!value || typeof value !== 'object') return [];
    const raw = value as Record<string, unknown>;
    const rawDoors = Array.isArray(raw.doors) ? raw.doors : [];
    return [{
      cabinet_id: typeof raw.cabinet_id === 'string' ? raw.cabinet_id : undefined,
      cabinet_name: typeof raw.cabinet_name === 'string' ? raw.cabinet_name : null,
      cabinetLocation:
        typeof raw.location === 'string'
          ? raw.location
          : typeof raw.cabinetLocation === 'string'
            ? raw.cabinetLocation
            : null,
      location: typeof raw.location === 'string' ? raw.location : null,
      latitude: (raw.latitude as string | number | null | undefined) ?? null,
      longitude: (raw.longitude as string | number | null | undefined) ?? null,
      capacity: typeof raw.capacity === 'number' ? raw.capacity : null,
      last_log_time: typeof raw.last_log_time === 'string' ? raw.last_log_time : null,
      lastConected: typeof raw.lastConected === 'string' ? raw.lastConected : null,
      doors: rawDoors.map((door) => {
        const d = door && typeof door === 'object' ? (door as Record<string, unknown>) : {};
        return {
          slot: typeof d.slot === 'number' ? d.slot : null,
          batterySn:
            typeof d.battery_sn === 'string'
              ? d.battery_sn
              : typeof d.batterySn === 'string'
                ? d.batterySn
                : null,
          batteryId:
            typeof d.battery_id === 'string'
              ? d.battery_id
              : typeof d.batteryId === 'string'
                ? d.batteryId
                : null,
          soc: typeof d.soc === 'number' ? d.soc : null,
          status: typeof d.status === 'string' ? d.status : null,
        };
      }),
    }];
  });
}

// ── Hidden cabinets ───────────────────────────────────────────────────────

/** Cabinets excluded from every surface (test rigs, virtual/relay units).
 *  Shared by the cabinets store and the fleet grouping so one list hides a
 *  rig from the station pins and the fleet glyphs alike. */
export const HIDDEN_CABINETS = ['SHASVIRTUALCABINET'];

const cabKey = (s?: string | null): string => (s ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');

/** True when the identifier (cabinet id, name, location, serial) is hidden. */
export function isHiddenCabinetId(id: string | null | undefined): boolean {
  const k = cabKey(id);
  return !!k && HIDDEN_CABINETS.some((h) => cabKey(h) === k);
}

/** True when the cabinet record matches the hidden list on any identifying field. */
export function isHiddenCabinet(c: {
  cabinet_id?: string;
  cabinetLocation?: string | null;
  display_name?: string;
  serial_number?: string;
}): boolean {
  return [c.cabinet_id, c.cabinetLocation, c.display_name, c.serial_number].some(isHiddenCabinetId);
}

// ── Derived model ─────────────────────────────────────────────────────────

/** Where a battery is seated, when it is sitting in a swap cabinet. */
export interface Seat {
  cabinetId: string;
  cabinetLocation: string | null;
  slot: number | null;
  soc: number | null;
}

/** Serial (trimmed + uppercased) → seat. */
export type Seating = Map<string, Seat>;

export type FleetState = 'moving' | 'parked' | 'inCabinet' | 'solo';

/**
 * Base pin/KPI color per state, reusing the survey map's own semantic
 * palette ($lib/constants) instead of inventing a second one — a color means
 * the same thing here as it does on the Map tab. Individual heatmap modes
 * may override a unit's color (FleetMode.pinColor); this is the fallback.
 */
export const STATE_COLOR: Record<FleetState, string> = {
  moving: PHASE.planned.color,
  parked: PHASE.existing.color,
  inCabinet: ASSET.cabinet.color,
  solo: ASSET.solar.color,
};

export interface FleetUnit {
  /** Stable key for Leaflet marker reuse — normalized pack serial, falling back to GPS ID. */
  id: string;
  serial: string;
  /** GPS-module identity, kept separate for telemetry/cell-diagnostics calls. */
  batteryId?: string;
  lat: number;
  lng: number;
  /** 0-100, clamped. */
  soc: number;
  speed: number;
  current: number;
  power: number;
  direction: number;
  dischargeMos: string | null;
  km30d: number;
  swaps30d: number;
  /** Backend deployment classification; absent only for legacy payloads. */
  deploymentState?: DeploymentState | null;
  /** Evidence supporting an inferred deployment, when applicable. */
  deploymentEvidence?: DeploymentEvidence | null;
  /** Time of the incomplete dispatch event, when supplied by the backend. */
  deploymentEventTime?: string | null;
  /** Minutes between this ping and the newest ping in the batch (never negative). */
  minutesSincePing: number;
  /** No fresh ping in FLEET.STALE_DAYS or more — orthogonal to state and SoC. */
  stale: boolean;
  state: FleetState;
  /** Present only when state === 'inCabinet'. */
  seat: Seat | null;
}

export interface FleetCounts {
  moving: number;
  parked: number;
  inCabinet: number;
  solo: number;
}

/**
 * Cluster donut only. Other networks' swap stations share the map but are not
 * our units: they have no silhouette, no SoC, and must never land in the
 * "in cabinet" wedge. They get their own member here rather than in
 * FleetState, which indexes SILHOUETTE/HOLLOW/STATE_COLOR and is a field on
 * FleetUnit — a 'station' there would be a lie at every one of those sites.
 * FleetCounts (KPI header, controls) stays station-free for the same reason.
 */
export type ClusterState = FleetState | 'station';
export type ClusterCounts = Record<ClusterState, number>;

/** Slate: reads as "on the map, not ours". */
export const STATION_COLOR = '#64748b';

/** Human-readable state names for marker tooltips and accessible names. */
export const STATE_LABEL: Record<FleetState, string> = {
  moving: 'Moving',
  parked: 'Parked',
  inCabinet: 'In cabinet',
  solo: 'Solo',
};

// ── Marker names ──────────────────────────────────────────────────────────
//
// Every glyph <svg> is aria-hidden: the shapes encode state for sighted users
// and carry no text. That leaves the Leaflet marker itself as the one place a
// name belongs — its `title` option is both the hover tooltip and the
// accessible name of the focusable element (markers are tabbable). Naming the
// SVG *as well* would make a screen reader announce the marker and then
// descend into the image and announce it again, so these builders are the
// single source of that one name.

export function unitLabel(u: FleetUnit): string {
  const stale = u.stale ? ', no recent ping' : '';
  return `${u.serial} — ${STATE_LABEL[u.state]}, ${u.soc}% charge${stale}`;
}

export function cabinetLabel(g: CabinetGroup): string {
  const n = `${g.count} ${g.count === 1 ? 'battery' : 'batteries'}`;
  const slots = g.totalSlots ? ` of ${g.totalSlots} doors` : '';
  return `Cabinet ${g.cabinetId} — ${n}${slots}`;
}

/** Another network's station: named, but explicitly not one of ours. */
export function stationLabel(network: string, name: string): string {
  return `${network} swap station — ${name}`;
}

export interface Classification {
  units: FleetUnit[];
  counts: FleetCounts;
  /** Records dropped for unusable coordinates — surfaced in the status bar. */
  rejected: number;
  /** Feed clock: newest lastlogtime in the batch, in ms. */
  reference: number;
}

// ── Helpers ───────────────────────────────────────────────────────────────

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

function num(v: unknown): number {
  const n = typeof v === 'number' ? v : Number.parseFloat(String(v ?? ''));
  return Number.isFinite(n) ? n : 0;
}

/** Both confirmed and telemetry-inferred assignments belong to a bike. */
export function isOnBikeDeployment(state: DeploymentState | null | undefined): boolean {
  return state === 'on_bike' || state === 'on_bike_inferred';
}

/** Pin scale factor for the current zoom, when auto-scaling is enabled. */
export function zoomScale(zoom: number): number {
  const raw = 1 + (zoom - FLEET.ZOOM_ANCHOR) * FLEET.ZOOM_STEP;
  return clamp(raw, FLEET.ZOOM_SCALE_MIN, FLEET.ZOOM_SCALE_MAX);
}

/**
 * Parse and sanity-check a record's coordinates.
 *
 * Mirrors positive Kenyan latitudes south (upstream sign defect), then rejects
 * null island and anything outside the operating bbox. Returns null to reject.
 */
export function normalizeCoords(
  rawLat: unknown,
  rawLng: unknown,
): { lat: number; lng: number } | null {
  let lat = Number.parseFloat(String(rawLat ?? ''));
  const lng = Number.parseFloat(String(rawLng ?? ''));
  if (lat > 0 && lat < FLEET.LAT_FLIP_MAX) lat = -lat;
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (lat === 0 || lng === 0) return null;
  const { latMin, latMax, lngMin, lngMax } = FLEET.BBOX;
  if (lat < latMin || lat > latMax || lng < lngMin || lng > lngMax) return null;
  return { lat, lng };
}

/**
 * A parsed `lastlogtime` is usable only if it isn't further ahead of
 * wall-clock than a broken device clock could plausibly explain via the
 * feed's normal skew — see `FLEET.MAX_LOG_SKEW_H`. A device with a bad
 * RTC/GPS fix can jump years into the future; treat that the same as "no
 * timestamp" everywhere `lastlogtime` is consumed.
 */
export function isSaneLogMs(ms: number): boolean {
  return Number.isFinite(ms) && ms <= Date.now() + FLEET.MAX_LOG_SKEW_H * 3_600_000;
}

/** Newest lastlogtime in the batch (feed clock), or 0 if the batch has none. */
export function latestLogTime(records: BatteryRecord[]): number {
  let max = 0;
  for (const r of records) {
    if (!r.lastlogtime) continue;
    const t = new Date(r.lastlogtime).getTime();
    if (isSaneLogMs(t) && t > max) max = t;
  }
  return max;
}

/**
 * `YYYY-MM-DD` for an instant, in the given IANA zone.
 *
 * heatmap.html used `new Date().toISOString().slice(0,10)` — a UTC date. Kenya
 * is UTC+3, so every record logged 00:00-03:00 EAT failed the same-day test and
 * was demoted from Parked to Solo/Storage.
 */
export function localDateStr(ms: number, tz: string = FLEET.TZ): string {
  // en-CA formats as YYYY-MM-DD, which is exactly the prefix we compare against.
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(ms));
}

/** Per-serial high-water mark: the newest accepted record and its log time. */
export type LogWatermarks = Map<string, { logMs: number; record: BatteryRecord }>;

/**
 * The feed occasionally re-sends a stale row for a unit that has already
 * reported something newer — measured at 3 units of 335, up to 11 minutes
 * back. Ingesting it makes a moving bike fail the freshness gate and drop out
 * of its layer for one poll. Reject any record older than the newest already
 * seen for that serial, carrying the last good record forward — a unit
 * dropped outright is a vanished pin, which is the bug.
 *
 * Records with no serial or no usable timestamp pass through unfiltered:
 * there is nothing to compare them against. An insane (`isSaneLogMs` fails)
 * timestamp counts as "no usable timestamp" too — otherwise a device with a
 * broken clock sets a watermark far in the future and every real reading it
 * sends afterward looks regressive forever.
 */
export function rejectRegressive(
  records: BatteryRecord[],
  prev: LogWatermarks,
): { records: BatteryRecord[]; watermarks: LogWatermarks } {
  const watermarks: LogWatermarks = new Map(prev);
  const out: BatteryRecord[] = [];
  for (const r of records) {
    const serial = normalizedBatterySerial(r);

    const parsed = r.lastlogtime ? new Date(r.lastlogtime).getTime() : Number.NaN;
    const logMs = isSaneLogMs(parsed) ? parsed : Number.NaN;
    if (!serial || !Number.isFinite(logMs)) {
      out.push(r);
      continue;
    }
    const seen = watermarks.get(serial);
    if (seen && logMs < seen.logMs) {
      out.push(seen.record);
      continue;
    }
    watermarks.set(serial, { logMs, record: r });
    out.push(r);
  }
  return { records: out, watermarks };
}

/** Index every battery serial currently seated in a cabinet door. */
export function seatingMap(cabinets: Cabinet[]): Seating {
  const seating: Seating = new Map();
  for (const cab of cabinets) {
    for (const door of cab.doors ?? []) {
      const sn = (door.batterySn ?? '').trim().toUpperCase();
      if (!sn) continue;
      seating.set(sn, {
        cabinetId: cab.cabinet_id ?? '',
        cabinetLocation: cab.cabinetLocation ?? null,
        slot: door.slot ?? null,
        soc: door.soc ?? null,
      });
    }
  }
  return seating;
}

// ── Geohashing (H4 bins / H5 RPC) ─────────────────────────────────────────

const GEOHASH_BASE32 = '0123456789bcdefghjkmnpqrstuvwxyz';

/**
 * Standard geohash (base32), truncated to 7 characters — roughly a 153m ×
 * 153m cell, the resolution the bin tiers in `traffic_bins` are keyed by.
 * Used both by the bin rollup and by the RPC's raw-window aggregation
 * (H5), so cell IDs agree everywhere a window boundary can hand off between
 * the two. Verified against the standard reference vector: `geohash7(57.64911,
 * 10.40744) === 'u4pruyd'` (matches geohash.org's 'u4pruydqqvj' truncated).
 */
export function geohash7(lat: number, lng: number): string {
  let idx = 0;
  let bit = 0;
  let evenBit = true;
  let hash = '';
  let latMin = -90;
  let latMax = 90;
  let lngMin = -180;
  let lngMax = 180;
  while (hash.length < 7) {
    if (evenBit) {
      const mid = (lngMin + lngMax) / 2;
      if (lng >= mid) {
        idx = idx * 2 + 1;
        lngMin = mid;
      } else {
        idx = idx * 2;
        lngMax = mid;
      }
    } else {
      const mid = (latMin + latMax) / 2;
      if (lat >= mid) {
        idx = idx * 2 + 1;
        latMin = mid;
      } else {
        idx = idx * 2;
        latMax = mid;
      }
    }
    evenBit = !evenBit;
    bit++;
    if (bit === 5) {
      hash += GEOHASH_BASE32.charAt(idx);
      bit = 0;
      idx = 0;
    }
  }
  return hash;
}

/** Center of a geohash7 cell — for placing an aggregated bin back on the map. */
export function geohash7Center(hash: string): { lat: number; lng: number } {
  let evenBit = true;
  let latMin = -90;
  let latMax = 90;
  let lngMin = -180;
  let lngMax = 180;
  for (const c of hash) {
    const idx = GEOHASH_BASE32.indexOf(c);
    for (let n = 4; n >= 0; n--) {
      const bitN = (idx >> n) & 1;
      if (evenBit) {
        const mid = (lngMin + lngMax) / 2;
        if (bitN === 1) lngMin = mid;
        else lngMax = mid;
      } else {
        const mid = (latMin + latMax) / 2;
        if (bitN === 1) latMin = mid;
        else latMax = mid;
      }
      evenBit = !evenBit;
    }
  }
  return { lat: (latMin + latMax) / 2, lng: (lngMin + lngMax) / 2 };
}

// ── Ingest (H2 collector) ────────────────────────────────────────────────

/** One row for `battery_pings` — the 48h raw fidelity buffer. */
export interface BatteryPingRow {
  serial: string;
  last_log_time: string;
  /** EWKT — Postgres/PostGIS parses this directly for a `geometry(Point,4326)` column. */
  location: string;
  speed: number;
  soc: number;
  seated: boolean;
}

/**
 * Transform one sparkapi record into a `battery_pings` row, or null to skip
 * it — the collector's ingest-time filter, tested here since Deno (the
 * sync-batteries Edge Function's runtime) can't share this Vite-bundled
 * module; keep `supabase/functions/sync-batteries/index.ts`'s copy in sync
 * with this one. Reuses `normalizeCoords` (bbox + latitude-mirror) and
 * `isSaneLogMs` (clock-skew bound) so a bad fix or a broken device clock
 * never enters the history: filtering either out of the bins afterwards is
 * impossible once aggregated.
 *
 * Unlike `classify()`, this only needs enough to store a ping — no live
 * state, no cabinet coordinates, just whether the serial is currently seated.
 */
export function toBatteryPing(record: BatteryRecord, seating: Seating): BatteryPingRow | null {
  if (record.feed === 'rust' && record.registered !== true) return null;
  if (record.feed !== 'rust' && record.registered === false) return null;
  const coords = normalizeCoords(record.latitude, record.longitude);
  if (!coords) return null;
  const serial = normalizedBatterySerial(record);
  if (!serial) return null;
  const logMs = record.lastlogtime ? new Date(record.lastlogtime).getTime() : Number.NaN;
  if (!isSaneLogMs(logMs)) return null;

  return {
    serial,
    last_log_time: new Date(logMs).toISOString(),
    location: `SRID=4326;POINT(${coords.lng} ${coords.lat})`,
    speed: num(record.speed),
    soc: clamp(Math.round(num(record.charge)), 0, 100),
    seated: seating.has(serial),
  };
}

// ── Classification ────────────────────────────────────────────────────────

export interface LogTiming {
  logMs: number;
  minutesSincePing: number;
  fresh: boolean;
  stale: boolean;
  loggedToday: boolean;
}

/** Compute timing, freshness, staleness, and same-day status for a battery record against feed clock. */
export function resolveLogTiming(
  r: BatteryRecord,
  reference: number,
  today: string | null,
): LogTiming {
  const parsedLog = r.lastlogtime ? new Date(r.lastlogtime).getTime() : 0;
  const logMs = isSaneLogMs(parsedLog) ? parsedLog : 0;
  // Clamp at 0: a record newer than the batch reference would read negative.
  const minutesSincePing =
    reference && logMs ? Math.max(0, (reference - logMs) / 60_000) : Number.POSITIVE_INFINITY;
  const fresh = minutesSincePing <= FLEET.FRESH_PING_MIN;
  // Both sides go through localDateStr: comparing the raw ISO string's prefix
  // (a UTC date) against a Nairobi date would misjudge every record logged
  // after 21:00 UTC, which is still the same working day in Kenya.
  const loggedToday = today !== null && logMs > 0 && localDateStr(logMs) === today;
  const stale = minutesSincePing >= FLEET.STALE_DAYS * 24 * 60;
  return { logMs, minutesSincePing, fresh, stale, loggedToday };
}

export interface DeploymentContractResolution {
  deploymentState: DeploymentState | null;
  deploymentEvidence: DeploymentEvidence | null;
  deploymentEventTime: string | null;
  hasDeploymentContract: boolean;
  onBike: boolean;
  mayMove: boolean;
}

/** Resolve deployment state and contract rules for a battery record. */
export function resolveDeploymentContract(
  r: BatteryRecord,
  worksThisMonth: boolean,
): DeploymentContractResolution {
  // The Rust feed's deployment state is authoritative for whether a pack
  // belongs to a bike. Legacy envelopes predate this field, so retain their
  // historical mileage/swap heuristic during a deliberate rollback window.
  const deploymentState =
    r.deploymentState === 'on_bike' ||
    r.deploymentState === 'on_bike_inferred' ||
    r.deploymentState === 'unassigned' ||
    r.deploymentState === 'unknown'
      ? r.deploymentState
      : null;
  const deploymentEvidence =
    r.deploymentEvidence === 'incomplete_dispatch_with_active_telemetry' ||
    r.deploymentEvidence === 'active_telemetry_without_dispatch_event'
      ? r.deploymentEvidence
      : null;
  const deploymentEventTime =
    typeof r.deploymentEventTime === 'string' ? r.deploymentEventTime : null;
  const hasDeploymentContract = r.feed === 'rust' || deploymentState !== null;
  const onBike = hasDeploymentContract ? isOnBikeDeployment(deploymentState) : worksThisMonth;
  const mayMove = hasDeploymentContract ? onBike : true;
  return {
    deploymentState,
    deploymentEvidence,
    deploymentEventTime,
    hasDeploymentContract,
    onBike,
    mayMove,
  };
}

/**
 * Multi-sensor moving detection:
 * 1. Kinetic motion: speed is at or above threshold without location drift flag
 * 2. Active load draw: drawing current (e.g. motor idle or accelerating at intersection)
 * 3. Fallback: speed > 0 when discharge MOSFET is active or not explicitly OFF
 */
export function detectMotorActive(
  speed: number,
  current: number,
  power: number,
  dischargeMos: string | null,
): boolean {
  const isDischarging = current <= FLEET.DISCHARGE_AMPS_THRESHOLD || power <= -100;
  const hasKineticSpeed = speed >= FLEET.MOTION_SPEED_THRESHOLD_KMH;
  const hasAnySpeed = speed > 0 && dischargeMos !== 'OFF';
  return hasKineticSpeed || isDischarging || hasAnySpeed;
}

/** Decide which of the 4 fleet states applies to a unit given sensor and contract state. */
export function resolveFleetState(
  seat: Seat | null,
  mayMove: boolean,
  fresh: boolean,
  motorActive: boolean,
  onBike: boolean,
  hasDeploymentContract: boolean,
  worksThisMonth: boolean,
  loggedToday: boolean,
): FleetState {
  if (seat) return 'inCabinet';
  if (mayMove && fresh && motorActive) return 'moving';
  if (onBike && (hasDeploymentContract || (worksThisMonth && loggedToday))) return 'parked';
  return 'solo';
}

/**
 * Sort every battery into one of four states, and count them.
 *
 *   inCabinet — serial is seated in a cabinet door right now
 *   moving    — not seated, speed > 0, and pinged within FRESH_PING_MIN
 *   parked    — a working bike at rest: has 30d mileage or swaps, logged today
 *   solo      — everything else: dormant depot stock
 *
 * Counts cover the whole fleet regardless of which layers are toggled on; layer
 * and mode filtering happens downstream in fleetModes/*.
 */
export function classify(records: BatteryRecord[], seating: Seating): Classification {
  const reference = latestLogTime(records);
  // An empty or timestamp-less batch still needs a baseline; the feed clock is
  // unavailable, so nothing can be "fresh" and everything falls through to solo.
  const today = reference ? localDateStr(reference) : null;

  const units: FleetUnit[] = [];
  const counts: FleetCounts = { moving: 0, parked: 0, inCabinet: 0, solo: 0 };
  let rejected = 0;

  for (const r of records) {
    if ((r.feed === 'rust' && r.registered !== true) || (r.feed !== 'rust' && r.registered === false)) {
      rejected++;
      continue;
    }
    const coords = normalizeCoords(r.latitude, r.longitude);
    if (!coords) {
      rejected++;
      continue;
    }

    const serial = normalizedBatterySerial(r);
    const batteryId = typeof r.batteryId === 'string' ? r.batteryId.trim() : '';

    const speed = num(r.speed);
    const current = num(r.current);
    const power = num(r.power);
    const direction = num(r.direction);
    const dischargeMos = r.dischargeMos ? String(r.dischargeMos).trim().toUpperCase() : null;
    const soc = clamp(Math.round(num(r.charge)), 0, 100);
    const km30d = num(r.effKm30d);
    const swaps30d = Math.trunc(num(r.effPairedSwaps));

    const { minutesSincePing, fresh, stale, loggedToday } = resolveLogTiming(
      r,
      reference,
      today,
    );

    const seat = serial ? (seating.get(serial) ?? null) : null;
    const worksThisMonth = km30d > FLEET.PARKED_MIN_KM_30D || swaps30d > 0;
    const {
      deploymentState,
      deploymentEvidence,
      deploymentEventTime,
      hasDeploymentContract,
      onBike,
      mayMove,
    } = resolveDeploymentContract(r, worksThisMonth);

    const motorActive = detectMotorActive(speed, current, power, dischargeMos);

    const state = resolveFleetState(
      seat,
      mayMove,
      fresh,
      motorActive,
      onBike,
      hasDeploymentContract,
      worksThisMonth,
      loggedToday,
    );

    counts[state]++;
    units.push({
      id: serial || r.batteryId || `${coords.lat},${coords.lng}`,
      serial,
      batteryId: batteryId || undefined,
      lat: coords.lat,
      lng: coords.lng,
      soc,
      speed,
      current,
      power,
      direction,
      dischargeMos,
      km30d,
      swaps30d,
      deploymentState,
      deploymentEvidence,
      deploymentEventTime,
      minutesSincePing,
      stale,
      state,
      seat,
    });
  }

  return { units, counts, rejected, reference };
}

// ── Heading derivation ────────────────────────────────────────────────────

/** Last position a unit's heading was measured from, plus that heading. */
export interface Track {
  lat: number;
  lng: number;
  /**
   * Degrees clockwise from north, or null when no bearing has been measured
   * yet. Null rather than 0: 0 is due north, a perfectly ordinary heading, so
   * a numeric sentinel is indistinguishable from data and every consumer
   * downstream ends up drawing an arrow that points somewhere the unit is not
   * going.
   */
  deg: number | null;
}

/**
 * Update per-unit travel headings from a fresh batch.
 *
 * Infers travel direction by comparing successive GPS positions, with hardware
 * compass `direction` as a direct heading when displacement is still accumulating.
 */
export function trackHeadings(units: FleetUnit[], prev: Map<string, Track>): Map<string, Track> {
  const next = new Map<string, Track>();
  for (const u of units) {
    const was = prev.get(u.id);
    const hwHeading = u.direction > 0 && u.direction <= 360 ? u.direction : null;
    if (!was) {
      next.set(u.id, { lat: u.lat, lng: u.lng, deg: hwHeading });
      continue;
    }
    if (haversineM(was.lat, was.lng, u.lat, u.lng) >= FLEET.HEADING_MIN_M) {
      next.set(u.id, {
        lat: u.lat,
        lng: u.lng,
        deg: bearingDeg(was.lat, was.lng, u.lat, u.lng),
      });
    } else {
      next.set(u.id, {
        lat: was.lat,
        lng: was.lng,
        // ?? rather than ||: a measured due-north bearing is 0, which is falsy,
        // and || would throw it away in favour of the hardware compass.
        deg: was.deg ?? hwHeading,
      });
    }
  }
  return next;
}

/** A unit's buffered positions within the trailing MOVING_WINDOW_MIN. */
export type MotionBuffers = Map<string, { t: number; lat: number; lng: number }[]>;

/**
 * Decide `moving` from displacement over FLEET.MOVING_WINDOW_MIN rather than
 * a single packet — the real fix for the flicker (a `speed: 0` poll can no
 * longer flip the state, because it's one point among several in the
 * window). Applied on top of `classify()` rather than inside it, same
 * reasoning as the hysteresis this replaces: `classify()` stays pure and
 * well tested, and the decision needs the previous batch's buffered track.
 *
 * A segment implying more than MAX_SPEED_KMH is a GPS glitch (same guard
 * `bikes.svelte.ts` applies for the heat/dwell rollup) and contributes
 * nothing to the accumulated distance.
 *
 * Never applies to `inCabinet`: seating is authoritative, motion is
 * inferred, and a battery just seated must leave the motion layer
 * immediately.
 *
 * Corroborating evidence, not the decision: a unit with fewer than two
 * buffered points has no window to measure yet, so it falls back to
 * classify()'s own single-packet call (`fresh && isMotorActive`, read off
 * `u.state` before this function overrides it) rather than defaulting to
 * not-moving for its first window.
 */
export function deriveMotion(
  units: FleetUnit[],
  buffers: MotionBuffers,
  now: number,
): { units: FleetUnit[]; buffers: MotionBuffers } {
  const nextBuffers: MotionBuffers = new Map();
  const nextUnits = units.map((u) => {
    if (u.state === 'inCabinet') return u;
    // A returned/unknown Rust pack is explicitly solo. Do not let buffered
    // positions from an earlier on-bike assignment resurrect it as moving.
    if (u.state === 'solo') return u;

    const cutoff = now - FLEET.MOVING_WINDOW_MIN * 60_000;
    const prevPts = (buffers.get(u.id) ?? []).filter((p) => p.t >= cutoff);
    const pts = [...prevPts, { t: now, lat: u.lat, lng: u.lng }];
    nextBuffers.set(u.id, pts);

    if (pts.length < 2) return u; // no window yet: corroborate from classify()'s own call

    let distance = 0;
    for (let i = 1; i < pts.length; i++) {
      const a = pts[i - 1];
      const b = pts[i];
      const d = haversineM(a.lat, a.lng, b.lat, b.lng);
      const dtMs = b.t - a.t;
      const kmh = dtMs > 0 ? (d / (dtMs / 1000)) * 3.6 : Number.POSITIVE_INFINITY;
      if (kmh > MAX_SPEED_KMH) continue; // GPS glitch: not real displacement
      distance += d;
    }

    if (distance >= FLEET.MOVING_MIN_M) return { ...u, state: 'moving' as const };
    if (u.state !== 'moving') return u; // classify() already said parked/solo — leave it
    // classify()'s single packet called this poll moving, but the track
    // disagrees: fall back to the same non-motion split classify() itself
    // uses. A contract-backed on-bike pack is parked; legacy rows retain the
    // historical mileage/swap fallback.
    const demoted: FleetState =
      isOnBikeDeployment(u.deploymentState) ||
      (u.deploymentState == null && (u.km30d > FLEET.PARKED_MIN_KM_30D || u.swaps30d > 0))
        ? 'parked'
        : 'solo';
    return { ...u, state: demoted };
  });
  return { units: nextUnits, buffers: nextBuffers };
}

/** Recount states after deriveMotion may have changed some of them. */
export function countStates(units: FleetUnit[]): FleetCounts {
  const counts: FleetCounts = { moving: 0, parked: 0, inCabinet: 0, solo: 0 };
  for (const u of units) counts[u.state]++;
  return counts;
}

// ── Cabinet aggregation ──────────────────────────────────────────────────

/** Every battery seated in one cabinet, collapsed into a single map feature. */
export interface CabinetGroup {
  cabinetId: string;
  cabinetLocation: string | null;
  lat: number;
  lng: number;
  count: number;
  /** Mean SoC across the seated batteries, rounded. */
  avgSoc: number;
  /** Seated batteries, ordered by slot (unknown slots last). */
  units: FleetUnit[];
  /** Total doors in the physical cabinet, when known. Unknown → solid square, never guessed. */
  totalSlots?: number;
  /** Per-door detail from cabinet telemetry (slot order), when known independently
   *  of live battery units — e.g. a station with no matching battery hits yet. */
  doorSoc?: Array<{
    slot?: number;
    present: boolean;
    soc: number | null;
    status?: string | null;
  }>;
}

/**
 * Split units into cabinet groups and loose units.
 *
 * Every battery in a cabinet reports that cabinet's coordinates, so 124 seated
 * batteries draw as 124 pins stacked on ~20 points — the single biggest source
 * of clutter on the map, and one clustering can't fix because the points are
 * exactly coincident. One glyph per cabinet carrying a count is both less ink
 * and more information.
 */
export function groupByCabinet(units: FleetUnit[]): {
  groups: CabinetGroup[];
  loose: FleetUnit[];
} {
  const byId = new Map<string, CabinetGroup>();
  const loose: FleetUnit[] = [];

  for (const u of units) {
    // A seat with no cabinet id can't be grouped or labelled; draw it as-is.
    if (u.state !== 'inCabinet' || !u.seat?.cabinetId) {
      loose.push(u);
      continue;
    }
    // Hidden rigs (virtual/test cabinets) are dropped outright, not re-plotted
    // as loose pins — the batteries there are test fixtures, not fleet stock.
    if (isHiddenCabinetId(u.seat.cabinetId)) continue;
    const g = byId.get(u.seat.cabinetId);
    if (g) {
      g.units.push(u);
    } else {
      byId.set(u.seat.cabinetId, {
        cabinetId: u.seat.cabinetId,
        cabinetLocation: u.seat.cabinetLocation,
        // The cabinet's own position is filled from the authoritative cabinet
        // snapshot by FleetPins; battery GPS is only the degraded fallback.
        lat: u.lat,
        lng: u.lng,
        count: 0,
        avgSoc: 0,
        units: [u],
      });
    }
  }

  const groups = [...byId.values()];
  for (const g of groups) {
    g.units.sort((a, b) => (a.seat?.slot ?? Infinity) - (b.seat?.slot ?? Infinity));
    g.count = g.units.length;
    // Prefer the cabinet door's own SoC reading, which is what an operator sees
    // on the cabinet, and fall back to the battery's telemetry when absent.
    g.avgSoc = Math.round(
      g.units.reduce((sum, u) => sum + (u.seat?.soc ?? u.soc), 0) / (g.count || 1),
    );
  }
  return { groups, loose };
}
